import React, { useState, useRef } from 'react';
import { useAuth } from './AuthContext';
import { uploadFileToS3, downloadFileFromS3 } from '../utils/lambdaApi';

function CommentSection({ taskId, comments, onAddComment, onDeleteComment, onEditComment, onAddAttachment, canUploadAttachment = true }) {
  const { currentUser } = useAuth();
  const [commentText, setCommentText] = useState('');
  const [expandedComment, setExpandedComment] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pendingFiles, setPendingFiles] = useState([]);
  const fileInputRef = useRef(null);

  const handleCommentChange = (e) => {
    setCommentText(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevenir que el evento se propague
    
    if (!commentText.trim() && pendingFiles.length === 0) return;
    
    // Crear comentario con timestamp para control de edición
    const commentData = {
      text: commentText.trim() || (pendingFiles.length > 0 ? `📎 ${pendingFiles.length} archivo${pendingFiles.length > 1 ? 's' : ''} adjunto${pendingFiles.length > 1 ? 's' : ''}` : ''),
      timestamp: new Date().toISOString(),
      author: currentUser?.displayName || currentUser?.email || 'Usuario',
      authorId: currentUser?.uid,
      attachments: pendingFiles,
      isAttachmentOnly: !commentText.trim() && pendingFiles.length > 0
    };
    
    onAddComment(commentData);
    
    setCommentText('');
    setPendingFiles([]);
  };

  const handleDeleteComment = (commentId) => {
    if (onDeleteComment) {
      onDeleteComment(commentId);
    }
  };

  // Función para verificar si un comentario puede ser editado/eliminado (5 minutos)
  const canEditComment = (comment) => {
    if (!comment.timestamp || !currentUser || comment.authorId !== currentUser.uid) {
      return false;
    }
    
    const commentTime = new Date(comment.timestamp);
    const now = new Date();
    const diffMinutes = (now - commentTime) / (1000 * 60);
    
    return diffMinutes <= 5;
  };

  // Función para iniciar la edición de un comentario
  const startEditComment = (comment) => {
    setEditingComment(comment.id || `temp-${comment.timestamp}`);
    setEditText(typeof comment.text === 'object' ? comment.text.text : comment.text);
  };

  // Función para cancelar la edición
  const cancelEdit = () => {
    setEditingComment(null);
    setEditText('');
  };

  // Función para guardar la edición
  const saveEdit = (commentId) => {
    if (!editText.trim()) return;
    
    if (onEditComment) {
      onEditComment(commentId, editText.trim());
    }
    
    setEditingComment(null);
    setEditText('');
  };

  // Función para manejar la selección de archivos
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;
    
    // Filtrar archivos permitidos
    const allowedFiles = files.filter(file => {
      const fileType = file.type.toLowerCase();
      const fileName = file.name.toLowerCase();
      
      return fileType === 'application/pdf' || 
             fileName.endsWith('.pdf') ||
             fileType === 'application/msword' || 
             fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
             fileName.endsWith('.doc') || 
             fileName.endsWith('.docx') ||
             fileType === 'application/vnd.ms-excel' ||
             fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
             fileName.endsWith('.xls') ||
             fileName.endsWith('.xlsx') ||
             fileType.startsWith('image/') || 
             fileName.endsWith('.jpg') || 
             fileName.endsWith('.jpeg') || 
             fileName.endsWith('.png') || 
             fileName.endsWith('.gif') || 
             fileName.endsWith('.bmp') || 
             fileName.endsWith('.webp');
    });
    
    if (allowedFiles.length === 0) {
      alert('Solo se permiten archivos PDF, Word (.doc, .docx), Excel (.xls, .xlsx) e imágenes (.jpg, .jpeg, .png, .gif, .bmp, .webp).');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const uploadedFiles = [];
      
      for (let i = 0; i < allowedFiles.length; i++) {
        const file = allowedFiles[i];
        setUploadProgress(Math.round((i / allowedFiles.length) * 100));
        const result = await uploadFileToS3(file);
        let fileRef = file.name;
        if (result?.body && typeof result.body === 'string') {
          try {
            const parsedBody = JSON.parse(result.body);
            if (parsedBody.key) fileRef = parsedBody.key;
          } catch (e) {
            // ignore parse error
          }
        }
        uploadedFiles.push({
          name: fileRef,
          size: file.size,
          uploadedBy: currentUser?.displayName || currentUser?.email || 'Usuario',
          uploadedById: currentUser?.uid,
          uploadDate: new Date().toISOString()
        });
      }
      
      // Agregar archivos a la lista de archivos pendientes
      setPendingFiles(prev => [...prev, ...uploadedFiles]);
      
      // También notificar sobre los archivos adjuntos si hay callback
      if (onAddAttachment) {
        onAddAttachment(uploadedFiles.map(f => f.name));
      }
      
    } catch (error) {
      console.error('Error al subir archivos:', error);
      alert(`Error al subir archivos: ${error.message}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      e.target.value = null;
    }
  };

  // Función para abrir selector de archivos
  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  // Función para remover un archivo pendiente
  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatFullDate = (dateString) => {
    const options = { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const toggleExpandComment = (commentId) => {
    if (expandedComment === commentId) {
      setExpandedComment(null);
    } else {
      setExpandedComment(commentId);
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    
    switch(extension) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'xls':
      case 'xlsx':
        return '📊';
      case 'ppt':
      case 'pptx':
        return '📑';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return '🖼️';
      case 'zip':
      case 'rar':
      case '7z':
        return '🗜️';
      case 'txt':
        return '📃';
      default:
        return '📎';
    }
  };

  return (
    <div className="comment-section">
      <h4>Comentarios</h4>
      
      <div className="comments-list">
        {comments && comments.length > 0 ? (
          comments.map(comment => {
            const commentId = comment.id || `temp-${comment.timestamp}`;
            const isEvidence = comment.isEvidence || false;
            return (
              <div 
                key={commentId} 
                className={`comment-item ${expandedComment === commentId ? 'expanded' : ''} ${isEvidence ? 'evidence-comment' : ''}`}
                onClick={() => toggleExpandComment(commentId)}
              >
                <div className="comment-header">
                  <div className="comment-author">
                    <span className="user-icon">{isEvidence ? '📋' : comment.isAttachmentOnly ? '📎' : '👤'}</span>
                    <span className="author-name">{comment.author || 'Usuario'}</span>
                    {isEvidence && <span className="evidence-badge">Evidencia</span>}
                    {comment.isAttachmentOnly && <span className="attachment-badge">Archivo</span>}
                  </div>
                  <div className="comment-actions-container">
                    <div className="comment-date" title={formatFullDate(comment.timestamp)}>
                      <span className="time-icon">🕒</span>
                      <span>{formatDate(comment.timestamp)}</span>
                    </div>
                    {currentUser && comment.authorId === currentUser.uid && canEditComment(comment) && onEditComment && onDeleteComment && (
                      <div className="comment-actions">
                        {!comment.isAttachmentOnly && (
                          <button 
                            className="edit-comment-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditComment(comment);
                            }}
                            title="Editar comentario"
                          >
                            ✏️
                          </button>
                        )}
                        <button 
                          className="delete-comment-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteComment(commentId);
                          }}
                          title="Eliminar comentario"
                        >
                          ✖
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="comment-text">
                  {editingComment === commentId ? (
                    <div className="comment-edit-form">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="comment-edit-input"
                        autoFocus
                      />
                      <div className="comment-edit-actions">
                        <button 
                          className="save-edit-btn"
                          onClick={() => saveEdit(commentId)}
                          disabled={!editText.trim()}
                        >
                          Guardar
                        </button>
                        <button 
                          className="cancel-edit-btn"
                          onClick={cancelEdit}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    (() => {
                      if (typeof comment.text === 'object' && comment.text !== null && typeof comment.text.text === 'string') {
                        return comment.text.text;
                      }
                      else if (typeof comment.text === 'string') {
                        return comment.text;
                      }
                      else {
                        return JSON.stringify(comment.text);
                      }
                    })()
                  )}
                </div>
                
                {comment.attachments && comment.attachments.length > 0 && (
                  <div className="comment-attachments">
                    {comment.attachments.map((file, index) => {
                      // Evitar redundancia: no mostrar "Subido por" ni fecha cuando coinciden con el autor y fecha del comentario
                      const isSameUploader = file.uploadedById === comment.authorId || 
                        (file.uploadedBy && comment.author && String(file.uploadedBy).toLowerCase() === String(comment.author).toLowerCase());
                      const commentTime = comment.timestamp ? new Date(comment.timestamp).getTime() : 0;
                      const uploadTime = file.uploadDate ? new Date(file.uploadDate).getTime() : 0;
                      const isSameTime = Math.abs(commentTime - uploadTime) < 60000; // mismo minuto
                      const showUploader = file.uploadedBy && !isSameUploader;
                      const showDate = file.uploadDate && !(isSameUploader && isSameTime);

                      return (
                        <div key={`${commentId}-attachment-${file.name || index}`} className="attachment-item">
                          <div className="attachment-info">
                            <span className="file-icon">{getFileIcon(file.name)}</span>
                            <button 
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                downloadFileFromS3(file.name);
                              }}
                              className="file-name attachment-link"
                              title={`Descargar ${file.name}`}
                            >
                              {file.name}
                            </button>
                            <span className="file-size">({Math.round(file.size / 1024)} KB)</span>
                          </div>
                          {(showUploader || showDate) && (
                            <>
                              {showUploader && (
                                <div className="attachment-uploader">
                                  <span className="uploader-icon">👤</span>
                                  <span className="uploader-name">Subido por: {file.uploadedBy}</span>
                                </div>
                              )}
                              {showDate && (
                                <div className="attachment-date">
                                  <span className="date-icon">📅</span>
                                  <span className="date-text">{formatDate(file.uploadDate)}</span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <p className="no-comments">No hay comentarios aún.</p>
        )}
      </div>
      
      {currentUser && onAddComment && (
        <form className="comment-form" onSubmit={handleSubmit}>
          <textarea
            placeholder="Escribe un comentario..."
            value={commentText}
            onChange={handleCommentChange}
            className="comment-input"
          />
          
          {isUploading && (
            <div className="upload-progress-container">
              <div 
                className="upload-progress-bar" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
              <div className="upload-status-text">
                Subiendo archivo{uploadProgress < 100 ? '...' : ' completado!'}
              </div>
            </div>
          )}

          {pendingFiles.length > 0 && (
            <div className="pending-files-preview">
              <div className="pending-files-header">
                <span>📎 Archivos seleccionados ({pendingFiles.length})</span>
              </div>
              <div className="pending-files-list">
                {pendingFiles.map((file, index) => (
                  <div key={index} className="pending-file-item">
                    <span className="file-icon">{getFileIcon(file.name)}</span>
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">({Math.round(file.size / 1024)} KB)</span>
                    <button 
                      type="button"
                      className="remove-file-btn"
                      onClick={() => removePendingFile(index)}
                      title="Remover archivo"
                    >
                      ✖
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="comment-form-actions">
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.bmp,.webp"
            />
            {canUploadAttachment && (
              <button 
                type="button"
                className="attach-file-btn"
                onClick={handleAttachClick}
                disabled={isUploading}
                title="Adjuntar archivos"
              >
                📎 Adjuntar
              </button>
            )}
            <button 
              type="submit" 
              className="submit-comment-btn"
              disabled={(!commentText.trim() && pendingFiles.length === 0) || isUploading}
            >
              {pendingFiles.length > 0 && commentText.trim() ? 'Comentar y Adjuntar' : 
               pendingFiles.length > 0 ? 'Adjuntar' : 'Comentar'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default CommentSection; 